import PropTypes from "prop-types"

/**
 * PropTypes for the Customers SidebarSection component.
 *
 * @property {Object|null} selectedCustomer - Currently selected customer object (null if no selection)
 * @property {Object} filters - Current filter state
 * @property {Function} onFilterChange - Filter change dispatcher
 * @property {number} visibleCount - Total number of customers matching the current filters
 * @property {Function} onEdit - Opens the edit form for the selected customer
 * @property {Function} onViewDetails - Opens the fiche modal for the selected customer
 * @property {Function} addToast - Toast helper, used by the Actions tab.
 * @property {Function} onSuspended - Called after a successful suspend, to refetch the list.
 */
export const customersSidebarSectionPropTypes = {
	selectedCustomer: PropTypes.object,
	filters: PropTypes.object.isRequired,
	onFilterChange: PropTypes.func.isRequired,
	visibleCount: PropTypes.number.isRequired,
	onEdit: PropTypes.func.isRequired,
	onViewDetails: PropTypes.func.isRequired,
	addToast: PropTypes.func.isRequired,
	onSuspended: PropTypes.func.isRequired,
}

/**
 * PropTypes for the StatusPill component.
 *
 * @property {string} label - Visible text.
 * @property {"success"|"warning"|"danger"|"neutral"} [tone] - Color token.
 */
export const statusPillPropTypes = {
	label: PropTypes.string.isRequired,
	tone: PropTypes.oneOf(["success", "warning", "danger", "neutral"]),
}

const customerFichePayloadShape = PropTypes.object

/**
 * PropTypes for the CustomerDataTab component.
 *
 * @property {Object} customer - Fiche detail payload.
 */
export const customerDataTabPropTypes = {
	customer: customerFichePayloadShape.isRequired,
}

/**
 * PropTypes for the CustomerContractTab component.
 *
 * @property {Object} customer - Fiche detail payload including `contracts`.
 */
export const customerContractTabPropTypes = {
	customer: customerFichePayloadShape.isRequired,
}

/**
 * PropTypes for the CustomerTokensTab component.
 *
 * @property {Object} customer - Fiche detail payload including `tokens`.
 */
export const customerTokensTabPropTypes = {
	customer: customerFichePayloadShape.isRequired,
}

/**
 * PropTypes for the CustomerBalanceTab component.
 *
 * @property {Object} customer - Fiche detail payload including `balance`.
 * @property {string} documentId - Customer document id.
 * @property {Function} addToast - Toast helper.
 * @property {Function} [onOpenSession] - Opens the originating session on the Sessions tab.
 * @property {Function} onBalanceChanged - Called after a successful mutation, to refetch the fiche.
 */
export const customerBalanceTabPropTypes = {
	customer: customerFichePayloadShape.isRequired,
	documentId: PropTypes.string.isRequired,
	addToast: PropTypes.func.isRequired,
	onOpenSession: PropTypes.func,
	onBalanceChanged: PropTypes.func,
}

/**
 * PropTypes for the CustomerSessionsTab component.
 *
 * @property {string} documentId - Customer document id.
 * @property {Function} addToast - Toast helper.
 * @property {string} [highlightSessionId] - Session document or session_id to highlight after a ledger click.
 */
export const customerSessionsTabPropTypes = {
	documentId: PropTypes.string.isRequired,
	addToast: PropTypes.func.isRequired,
	highlightSessionId: PropTypes.string,
}

/**
 * PropTypes for the CustomerInvoicesTab component.
 *
 * @property {string} documentId - Customer document id.
 * @property {Function} addToast - Toast helper.
 */
export const customerInvoicesTabPropTypes = {
	documentId: PropTypes.string.isRequired,
	addToast: PropTypes.func.isRequired,
}

/**
 * PropTypes for the CustomerSuspendModal component.
 *
 * @property {boolean} isOpen - Whether the modal is visible.
 * @property {Function} onClose - Close handler.
 * @property {Function} onConfirmSuspend - Confirm handler.
 * @property {Object} customer - Customer to suspend, for the confirmation copy.
 * @property {string} customer.name - Customer display name.
 */
export const customerSuspendModalPropTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onConfirmSuspend: PropTypes.func.isRequired,
	customer: PropTypes.shape({
		name: PropTypes.string,
	}),
	submitting: PropTypes.bool,
}

/**
 * PropTypes for the CustomerAssignCreditModal component.
 *
 * @property {boolean} isOpen - Whether the modal is visible.
 * @property {Function} onClose - Close handler.
 * @property {Function} onConfirmAssign - Called with `{ value, description }` on confirm.
 * @property {Object} customer - Customer the credit is being assigned to.
 * @property {string} customer.name - Customer display name.
 * @property {string} [currency] - Currency code shown next to the value.
 */
export const customerAssignCreditModalPropTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onConfirmAssign: PropTypes.func.isRequired,
	customer: PropTypes.shape({
		name: PropTypes.string,
	}),
	currency: PropTypes.string,
	submitting: PropTypes.bool,
}

/**
 * PropTypes for the CustomerTopupConfigModal component.
 *
 * @property {boolean} isOpen - Whether the modal is visible.
 * @property {Function} onClose - Close handler.
 * @property {Function} onConfirmSave - Called with the updated top-up fields on save.
 * @property {Object} balance - Current balance, for prefilling the form.
 * @property {string} [currency] - Currency code shown next to money fields.
 */
export const customerTopupConfigModalPropTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onConfirmSave: PropTypes.func.isRequired,
	balance: PropTypes.object,
	currency: PropTypes.string,
	submitting: PropTypes.bool,
}

/**
 * PropTypes for the CustomerFormModal component.
 *
 * @property {boolean} isOpen - Whether the modal is visible.
 * @property {Function} onClose - Close handler.
 * @property {Function} onSave - Async save handler.
 * @property {Object|null} [customer] - Fiche payload when editing, null when creating.
 * @property {string} [formError] - Server-side error to show inline.
 * @property {boolean} [saving] - Disables the submit button while the parent request is in flight.
 */
export const customerFormModalPropTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	customer: PropTypes.object,
	formError: PropTypes.string,
	saving: PropTypes.bool,
}

/**
 * PropTypes for the CustomerFicheModal component.
 *
 * @property {boolean} isOpen - Whether the modal is visible.
 * @property {Function} onClose - Close handler.
 * @property {string|null} documentId - Customer document id to load, or null while closed.
 */
export const customerFicheModalPropTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	documentId: PropTypes.string,
}
